import type { ReactionLookupAttribute } from '@gik/api/stream/useStream';
import { dotnetApiCached } from '@gik/core/api/ky/dotnetApi';
import type {
  EnrichedReactionAPIResponse,
  FlatActivity,
  GetFeedOptions,
  PostActivity,
  Reaction,
  StreamComment,
  StreamGetFeedOptions,
  UserUpdateActivity,
} from '../types/Stream';
import { FeedType as FeedGroupName } from '../types/Stream';

const defaultFeedOptions: GetFeedOptions = {
  withReactionCounts: true,
  withRecentReactions: true,
  enrich: true,
  withOwnChildren: true,
  recentReactionsLimit: 5,
};

export default class StreamClient {
  /**
   * Retrieve from a hash set rather than the global cache storage
   */
  private static _cacheHash: string;
  static setCacheHash(cacheHash: string) {
    StreamClient._cacheHash = cacheHash;
  }

  static async getUserUpdates(pageRouteId: string, options?: GetFeedOptions): Promise<UserUpdateActivity[]> {
    return this.getFeed<UserUpdateActivity>(FeedGroupName.UserUpdates, pageRouteId, options);
  }

  static async getPosts(pageRouteId: string, options?: GetFeedOptions): Promise<PostActivity[]> {
    return this.getFeed<PostActivity>(FeedGroupName.Posts, pageRouteId, options);
  }

  static async getPost(activityId: string): Promise<PostActivity> {
    return StreamClient._getActivity(activityId);
  }

  static async getReaction(reactionId: string): Promise<EnrichedReactionAPIResponse> {
    StreamClient._checkCacheHash();
    return dotnetApiCached
      .get(`stream/reactions/${reactionId}`, {
        searchParams: {
          hash: StreamClient._cacheHash,
        },
      })
      .json<EnrichedReactionAPIResponse>();
  }

  static async getReactions<T>(
    attributeType: ReactionLookupAttribute,
    attributeId,
    kind?: string,
    options?: GetFeedOptions
  ) {
    StreamClient._checkCacheHash();
    let url = `stream/reactions/${attributeType}_id/${attributeId}`;
    if (kind) {
      url += `/${kind}`;
    }
    return dotnetApiCached
      .get(url, {
        searchParams: {
          ...StreamClient._getPagingOptions(options),
          hash: StreamClient._cacheHash,
        },
      })
      .json<T[]>();
  }

  static async getComments(attributeType: ReactionLookupAttribute, attributeId?: string, options?: GetFeedOptions) {
    return this.getReactions<Reaction<StreamComment>>(attributeType, attributeId, 'comment', options);
  }

  static async getUserUpdate(userUpdateId: string): Promise<UserUpdateActivity> {
    return StreamClient._getActivity(userUpdateId);
  }

  /**
   * @param feedGroupName the name of the feed group, e.g. user updates or posts
   * @param feedId the ID of the feed, normally an inkind page route ID
   * @param pagingOptions paging options
   * @returns a list of stream activities
   */
  public static async getFeed<T extends FlatActivity>(
    feedGroupName: FeedGroupName,
    feedId: string,
    pagingOptions: GetFeedOptions = {}
  ): Promise<T[]> {
    StreamClient._checkCacheHash();
    return dotnetApiCached
      .get(`stream/feeds/${feedGroupName}/${feedId}`, {
        searchParams: {
          ...defaultFeedOptions,
          ...StreamClient._getPagingOptions(pagingOptions),
          hash: StreamClient._cacheHash,
        },
      })
      .json<T[]>();
  }

  private static _getActivity<T extends FlatActivity>(activityId: string) {
    StreamClient._checkCacheHash();
    return dotnetApiCached
      .get(`stream/activities/${activityId}`, {
        searchParams: {
          hash: StreamClient._cacheHash,
        },
      })
      .json<T>();
  }

  private static _getPagingOptions(options: GetFeedOptions): StreamGetFeedOptions {
    return {
      limit: options?.allPages ? undefined : options?.perPage || 99,
      allPages: options?.allPages,
      offset: (options?.offset || 0) + (options?.page && options?.perPage ? (options.page - 1) * options.perPage : 0),
      id_lt: options?.idLessThan,
      id_lte: options?.idLessOrEqualThan,
      id_gt: options?.idGreaterThan,
      id_gte: options?.idGreaterOrEqualThan,
    };
  }

  private static _checkCacheHash() {
    if (!StreamClient._cacheHash) {
      throw new Error('no cache hash set');
    }
  }
}
